/**
 * shadcn-style <Table> primitive (portal-imprint, slice 1).
 *
 * Net-new: CC had no Table primitive. Under the hybrid layout (D1) the
 * terminal/census books (Placed, Settled, Closed, Dropped, Rejected) render as
 * portal-style tables. Styled to the Case Portal's table CSS (portal.html:
 * 105-121): uppercase 11px header on a muted strip, 1px row rules, last row
 * borderless, whole-row hover.
 *
 * D4 REVERSED (ADR-0138): the portal's warm orange row hover is adopted — the
 * hover rides the var-driven `--row-hover` wash and selection rides the
 * `--active-wash` lane, so ground candidates re-pitch both.
 *
 * Consumed starting in slice 2 (first terminal book → table tracer).
 */
import * as React from "react";
import { cn } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  // .table-wrap — horizontal scroll on overflow.
  <div className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      className={cn("w-full border-separate border-spacing-0 text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn(className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  // Last row drops its bottom rule (portal: tbody tr:last-child td border none).
  <tbody ref={ref} className={cn("[&_tr:last-child_td]:border-0", className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "cursor-pointer transition-colors hover:bg-row-hover data-[state=selected]:bg-active-wash",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "whitespace-nowrap border-b-2 bg-muted px-3.5 py-2.5 text-left align-middle text-[11px] font-bold uppercase tracking-wider text-muted-foreground",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("border-b px-3.5 py-3 align-middle text-foreground", className)}
    {...props}
  />
));
TableCell.displayName = "TableCell";

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
